<template>
  <CustomModal
    :is-modal-open="isOpen"
    :overlay="'dark'"
    :with-header="true"
    :with-footer="true"
    :custom-classes="'tw:w-[100vw] tw:md:w-[768px] tw:md:max-w-[calc(100vw-32px)] tw:min-h-[600px] tw:h-[100vh] tw:md:h-auto tw:md:rounded-xl tw:rounded-none'"
    @toggleModal="requestClose">
    <template #modalHeader>
      <h2 :id="titleId" class="tw:text-xl">
        {{ $t(isEdit ? 'app_description_edit_description' : 'app_description_create_title') }}
      </h2>
    </template>

    <div class="tw:flex tw:w-full tw:min-h-[420px] tw:flex-col">
      <div class="tw:flex tw:items-center tw:justify-between tw:gap-3">
        <p>
          <strong>{{ $t('app_description_language') }}:</strong> {{ languageLabel }}
        </p>
        <button
          type="button"
          class="tw:flex tw:items-center tw:gap-1 tw:text-sm tw:cursor-pointer"
          :aria-expanded="isTipOpen"
          aria-controls="property-description-form-tip"
          @click="isTipOpen = !isTipOpen">
          <span>{{ $t('description_tip') }}</span>
          <ChevronDown
            class="tw:shrink-0 tw:transition-transform tw:duration-300 tw:w-[10px] tw:h-[7px]"
            :class="isTipOpen ? 'tw:rotate-180' : ''"
            fill="none"
            stroke="currentColor"
          />
        </button>
      </div>

      <div
        id="property-description-form-tip"
        class="tw:grid tw:transition-all tw:duration-300 tw:ease-in-out"
        :class="isTipOpen ? 'tw:grid-rows-[1fr] tw:mt-3' : 'tw:grid-rows-[0fr]'">
        <div class="tw:overflow-hidden">
          <div
            class="tw:grid tw:grid-cols-[24px_1fr] tw:gap-2 tw:rounded-lg tw:border tw:border-blue-300 tw:bg-blue-200 tw:p-3">
            <ArtificialIntelligenceOutlineIcon class="tw:shrink-0 tw:self-start tw:size-6" aria-hidden="true" />
            <div>
              <p class="tw:text-sm tw:font-bold">{{ $t('description_tip_header') }}</p>
              <p class="tw:text-sm">{{ $t('description_tip_subtext') }}</p>
            </div>
          </div>
        </div>
      </div>

      <PropertyDescriptionErrorBanner v-if="hasError" class="tw:mt-3" />

      <textarea
        v-model="descriptionText"
        class="tw:mt-3 tw:min-h-[220px] tw:w-full tw:flex-1 tw:resize-y tw:rounded-lg tw:border tw:border-gray-300 tw:p-3"
        :dir="descriptionTextDirection"
        :disabled="isSaving"
        :placeholder="$t('description_field_prompt')"
        :aria-label="$t(isEdit ? 'app_description_edit_description' : 'app_description_create_title')" />

      <p class="tw:mt-3 tw:text-xs" :class="isDescriptionOverLimit ? 'tw:text-red-700' : 'tw:text-gray-600'">
        {{
          $t('description_character_count', {
            CharacterNumber: formatNumber(descriptionText.length, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }),
            CharacterLimit: formatNumber(descriptionMaxLength, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }),
          })
        }}
      </p>
    </div>

    <template #modalFooter>
      <div class="tw:flex tw:w-full tw:items-center tw:justify-between">
        <GhostButton
          v-if="canRemove"
          size="big"
          class="tw:inline-flex tw:items-center tw:gap-2 tw:px-8 tw:py-2.5"
          :disabled="isSaving"
          @click="emit('remove', description)">
          <DeleteIcon class="tw:h-6 tw:w-6" aria-hidden="true" />
          <span>{{ $t('description_remove_cta') }}</span>
        </GhostButton>
        <div v-else />

        <div class="tw:flex tw:gap-2">
          <GhostButton size="big" class="tw:px-8 tw:py-2.5" :disabled="isSaving" @click="requestClose">
            {{ $t('cancel') }}
          </GhostButton>
          <MainButton size="big" class="tw:px-8 tw:py-2.5" :disabled="isSubmitDisabled" @click="submit">
            <span v-if="isSaving">
              <SmallDotLoader color="#fff" />
            </span>
            <span v-else>{{ $t(isEdit ? 'description_update_cta' : 'app_description_publish_now') }}</span>
          </MainButton>
        </div>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

import CustomModal from '@/components/base/CustomModal.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import ArtificialIntelligenceOutlineIcon from '@/components/icons/ArtificialIntelligenceOutlineIcon.svg?component'
import ChevronDown from '@/components/icons/ChevronDown.svg?component'
import DeleteIcon from '@/components/icons/Delete.svg?component'
import PropertyDescriptionErrorBanner from '@/components/propertyDetails/propertyDescriptions/PropertyDescriptionErrorBanner.vue'
import { useFormatting } from '@/composables/useFormatting'
import {
  DESCRIPTION_SOURCE,
  usePropertyDescriptions,
} from '@/components/propertyDetails/propertyDescriptions/usePropertyDescriptions.js'

const { formatNumber } = useFormatting()
const { getLanguageLabelByCode, getDescriptionTextDirection } = usePropertyDescriptions()

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
  description: {
    type: Object,
    default: null,
  },
  language: {
    type: Object,
    default: null,
  },
  draftDescription: {
    type: String,
    default: null,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close', 'close-with-changes', 'submit', 'remove'])

const descriptionText = ref('')
const isTipOpen = ref(true)

const titleId = 'property-description-form-title'
const descriptionMaxLength = 5000
const languageCode = computed(() => props.description?.languageCode ?? props.language?.language ?? '')
const descriptionTextDirection = computed(() => getDescriptionTextDirection(languageCode.value))
const languageLabel = computed(() => getLanguageLabelByCode(languageCode.value))
const canRemove = computed(() => props.isEdit && props.description?.source === DESCRIPTION_SOURCE.HOTELIER)
const initialDescriptionText = computed(() => props.description?.description ?? '')
const initialFormText = computed(() => props.draftDescription ?? initialDescriptionText.value)
const isDescriptionEmpty = computed(() => !descriptionText.value.trim())
const isDescriptionUnchanged = computed(() => props.isEdit && descriptionText.value === initialDescriptionText.value)
const isDescriptionOverLimit = computed(() => descriptionText.value.length > descriptionMaxLength)
const hasUnsavedChanges = computed(() => descriptionText.value !== initialDescriptionText.value)
const isSubmitDisabled = computed(
  () => isDescriptionEmpty.value || isDescriptionUnchanged.value || isDescriptionOverLimit.value || props.isSaving
)

watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      descriptionText.value = initialFormText.value
    }
  }
)

const requestClose = () => {
  if (props.isSaving) {
    return
  }

  if (hasUnsavedChanges.value) {
    emit('close-with-changes', getDescriptionPayload())
    return
  }

  emit('close')
}

const submit = () => {
  if (isSubmitDisabled.value) {
    return
  }

  emit('submit', getDescriptionPayload())
}

const getDescriptionPayload = () => ({
  isEdit: props.isEdit,
  descriptionDetails: props.description,
  language: props.language,
  description: descriptionText.value,
})
</script>
